import {
  CONDITION_OPERATORS,
  graphIds,
  type Condition,
  type ConditionOperator,
  type WorkflowContainerStep,
  type WorkflowDefinition,
  type WorkflowEntry,
  type WorkflowInput,
  type WorkflowStep,
} from '@agent-platform/shared';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { copyText } from '@/lib/browser';
import {
  ENTRY_META,
  INNER_TYPES,
  newLeaf,
  OPERATOR_LABELS,
  UNARY_OPERATORS,
  type WorkflowResources,
} from '@/lib/workflow-ui';

// --- Small editors ---

export function InputsEditor({
  inputs,
  disabled,
  onChange,
  noun = 'input',
  hint,
}: {
  inputs: WorkflowInput[];
  disabled: boolean;
  onChange: (inputs: WorkflowInput[]) => void;
  noun?: string;
  hint?: React.ReactNode;
}) {
  const update = (i: number, patch: Partial<WorkflowInput>) =>
    onChange(inputs.map((input, j) => (j === i ? { ...input, ...patch } : input)));
  return (
    <div className="grid gap-3">
      {hint && <p className="text-muted-foreground text-sm">{hint}</p>}
      {inputs.map((input, i) => (
        <div key={i} className="grid gap-2 rounded-md border p-2">
          <div className="grid grid-cols-[1fr_7rem_auto] gap-2">
            <Input
              aria-label={`${noun} name`}
              className="font-mono"
              value={input.name}
              disabled={disabled}
              onChange={(e) => update(i, { name: e.target.value })}
            />
            <Select
              value={input.type}
              onValueChange={(v) => update(i, { type: v as WorkflowInput['type'] })}
              disabled={disabled}
            >
              <SelectTrigger aria-label={`${noun} type`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['string', 'number', 'boolean', 'json'] as const).map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="icon"
              variant="ghost-destructive"
              aria-label={`Remove ${noun} ${input.name}`}
              disabled={disabled}
              onClick={() => onChange(inputs.filter((_, j) => j !== i))}
            >
              <Trash2 />
            </Button>
          </div>
          <Input
            aria-label={`${noun} description`}
            placeholder="Description (shown to the model and in the run form)"
            value={input.description}
            disabled={disabled}
            onChange={(e) => update(i, { description: e.target.value })}
          />
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={input.required}
              disabled={disabled}
              onChange={(e) => update(i, { required: e.target.checked })}
            />
            Required
          </label>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="justify-self-start"
        disabled={disabled}
        onClick={() =>
          onChange([
            ...inputs,
            {
              name: `${noun}_${inputs.length + 1}`,
              type: 'string',
              required: false,
              description: '',
            },
          ])
        }
      >
        <Plus aria-hidden /> Add {noun}
      </Button>
    </div>
  );
}

/** A JSON object of templates (tool arguments, nested workflow input). */
function TemplateMapEditor({
  id,
  label,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: Record<string, string>;
  disabled: boolean;
  onChange: (value: Record<string, string>) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        className="min-h-28 font-mono text-xs"
        value={text}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value || '{}') as unknown;
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
              throw new Error('object');
            setError(null);
            onChange(
              Object.fromEntries(
                Object.entries(parsed as Record<string, unknown>).map(([k, v]) => [
                  k,
                  typeof v === 'string' ? v : JSON.stringify(v),
                ]),
              ),
            );
          } catch {
            setError('Use a JSON object like {"city": "{{input.city}}"}.');
          }
        }}
      />
      {error ? (
        <p className="text-destructive text-xs">{error}</p>
      ) : (
        <p className="text-muted-foreground text-xs">
          A value that is exactly one placeholder keeps its type (numbers, lists, objects).
        </p>
      )}
    </div>
  );
}

export function ConditionEditor({
  condition,
  disabled,
  onChange,
  label,
}: {
  condition: Condition;
  disabled: boolean;
  onChange: (condition: Condition) => void;
  label?: string;
}) {
  const setRule = (i: number, patch: Partial<Condition['rules'][number]>) =>
    onChange({
      ...condition,
      rules: condition.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)),
    });
  return (
    <div className="grid gap-2" role="group" aria-label={label ?? 'Condition'}>
      {condition.rules.length > 1 && (
        <Select
          value={condition.match}
          onValueChange={(v) => onChange({ ...condition, match: v as Condition['match'] })}
          disabled={disabled}
        >
          <SelectTrigger aria-label="Match" className="h-8 w-48 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All rules hold (and)</SelectItem>
            <SelectItem value="any">Any rule holds (or)</SelectItem>
          </SelectContent>
        </Select>
      )}
      {condition.rules.map((rule, i) => (
        <div key={i} className="grid gap-1.5 rounded-md border p-2">
          <div className="flex gap-1.5">
            <Input
              aria-label="Value"
              className="h-8 font-mono text-xs"
              value={rule.left}
              placeholder="{{prev.status}}"
              disabled={disabled}
              onChange={(e) => setRule(i, { left: e.target.value })}
            />
            <Button
              size="icon"
              variant="ghost-destructive"
              className="size-8"
              aria-label="Remove rule"
              disabled={disabled || condition.rules.length === 1}
              onClick={() =>
                onChange({ ...condition, rules: condition.rules.filter((_, j) => j !== i) })
              }
            >
              <Trash2 />
            </Button>
          </div>
          <div className="grid grid-cols-[9rem_1fr] gap-1.5">
            <Select
              value={rule.operator}
              onValueChange={(v) => setRule(i, { operator: v as ConditionOperator })}
              disabled={disabled}
            >
              <SelectTrigger aria-label="Operator" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDITION_OPERATORS.map((op) => (
                  <SelectItem key={op} value={op}>
                    {OPERATOR_LABELS[op]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!UNARY_OPERATORS.includes(rule.operator) && (
              <Input
                aria-label="Compare with"
                className="h-8 font-mono text-xs"
                value={rule.right}
                disabled={disabled}
                onChange={(e) => setRule(i, { right: e.target.value })}
              />
            )}
          </div>
        </div>
      ))}
      <Button
        size="sm"
        variant="ghost"
        className="justify-self-start"
        disabled={disabled || condition.rules.length >= 10}
        onClick={() =>
          onChange({
            ...condition,
            rules: [...condition.rules, { left: '{{prev}}', operator: 'not_empty', right: '' }],
          })
        }
      >
        <Plus aria-hidden /> Add rule
      </Button>
    </div>
  );
}

/** Placeholders a step may use at its position (click to copy). */
function References({
  definition,
  index,
  scope,
}: {
  definition: WorkflowDefinition;
  /** Index of the top-level entry the step belongs to. */
  index: number;
  scope?: 'loop' | 'foreach';
}) {
  const refs = [
    ...definition.inputs.map((i) => `{{input.${i.name}}}`),
    ...(index > 0 ? ['{{prev}}'] : []),
    ...(scope === 'foreach' ? ['{{item}}', '{{index}}'] : []),
    ...(scope === 'loop' ? ['{{iteration}}'] : []),
    ...graphIds(definition.graph.slice(0, index)).map((id) => `{{steps.${id}.output}}`),
    '{{context.key}}',
  ];
  return (
    <div className="text-muted-foreground grid gap-1 text-xs">
      <span>Available values (click to copy):</span>
      <div className="flex flex-wrap gap-1">
        {refs.map((r) => (
          <button
            key={r}
            type="button"
            className="bg-muted hover:bg-accent rounded px-1.5 py-0.5 font-mono"
            onClick={() => {
              void copyText(r).then(
                () => toast.success(`Copied ${r}`),
                () => toast.error(`Copy failed: ${r}`),
              );
            }}
          >
            {r}
          </button>
        ))}
      </div>
      <span>
        Read fields with <code>.output.field</code> or list items with <code>.output.0</code>.
      </span>
    </div>
  );
}

function ResourceSelect<T extends { id: string; name: string }>({
  id,
  label,
  value,
  items,
  disabled,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  items: T[];
  disabled: boolean;
  onChange: (id: string) => void;
  placeholder: string;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={id} aria-label={label}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              <span className="font-mono">{item.name}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const NameAndId = ({
  node,
  disabled,
  onChange,
}: {
  node: { id: string; name: string };
  disabled: boolean;
  onChange: (patch: { id?: string; name?: string }) => void;
}) => (
  <div className="grid grid-cols-2 gap-2">
    <div className="grid gap-1.5">
      <Label htmlFor="wf-step-name">Name</Label>
      <Input
        id="wf-step-name"
        value={node.name}
        disabled={disabled}
        onChange={(e) => onChange({ name: e.target.value })}
      />
    </div>
    <div className="grid gap-1.5">
      <Label htmlFor="wf-step-id">Id</Label>
      <Input
        id="wf-step-id"
        className="font-mono"
        value={node.id}
        disabled={disabled}
        onChange={(e) => onChange({ id: e.target.value })}
      />
    </div>
  </div>
);

// --- Leaf steps ---

function LeafFields({
  step,
  resources,
  selfId,
  disabled,
  set,
}: {
  step: WorkflowStep;
  resources: WorkflowResources;
  selfId: string;
  disabled: boolean;
  set: (patch: Partial<WorkflowStep>) => void;
}) {
  switch (step.type) {
    case 'agent':
      return (
        <>
          <div className="grid grid-cols-[1fr_8rem] gap-2">
            <ResourceSelect
              id="wf-agent"
              label="Agent"
              value={step.agentId}
              items={resources.agents}
              disabled={disabled}
              placeholder="Choose an agent"
              onChange={(agentId) => set({ agentId })}
            />
            <div className="grid gap-1.5">
              <Label htmlFor="wf-agent-version">Version</Label>
              <Select
                value={step.version}
                onValueChange={(v) => set({ version: v as 'draft' | 'published' })}
                disabled={disabled}
              >
                <SelectTrigger id="wf-agent-version" aria-label="Agent version">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="wf-agent-prompt">Message</Label>
            <Textarea
              id="wf-agent-prompt"
              className="min-h-28 font-mono text-xs"
              value={step.prompt}
              disabled={disabled}
              onChange={(e) => set({ prompt: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={step.output === 'json'}
              disabled={disabled}
              onChange={(e) => set({ output: e.target.checked ? 'json' : 'text' })}
            />
            Expect a JSON answer (later steps read its fields; anything else fails the step)
          </label>
        </>
      );
    case 'tool':
      return (
        <>
          <ResourceSelect
            id="wf-tool"
            label="Tool"
            value={step.toolId}
            items={resources.tools}
            disabled={disabled}
            placeholder="Choose a tool"
            onChange={(toolId) => set({ toolId })}
          />
          <TemplateMapEditor
            key={step.id}
            id="wf-tool-args"
            label="Arguments"
            value={step.args}
            disabled={disabled}
            onChange={(args) => set({ args })}
          />
        </>
      );
    case 'sandbox':
      return (
        <>
          <ResourceSelect
            id="wf-sandbox"
            label="Sandbox"
            value={step.sandboxId}
            items={resources.sandboxes}
            disabled={disabled}
            placeholder="Choose a sandbox"
            onChange={(sandboxId) => set({ sandboxId })}
          />
          <div className="grid gap-1.5">
            <Label htmlFor="wf-code">Code</Label>
            <Textarea
              id="wf-code"
              spellCheck={false}
              className="min-h-36 font-mono text-xs"
              value={step.code}
              disabled={disabled}
              onChange={(e) => set({ code: e.target.value })}
            />
            <p className="text-muted-foreground text-xs">
              A fixed program: it reads workflow values from <code>input.json</code> (
              <code>{'{ input, context, steps, prev, item }'}</code>), never from placeholders, so
              input and agent output cannot inject code. Output is{' '}
              <code>{'{ stdout, stderr, exitCode }'}</code>.
            </p>
          </div>
        </>
      );
    case 'transform':
      return (
        <div className="grid gap-1.5">
          <Label htmlFor="wf-transform">Output template</Label>
          <Textarea
            id="wf-transform"
            className="min-h-28 font-mono text-xs"
            value={step.output}
            disabled={disabled}
            onChange={(e) => set({ output: e.target.value })}
          />
          <p className="text-muted-foreground text-xs">
            Text that is a JSON object or list becomes structured output.
          </p>
        </div>
      );
    case 'workflow': {
      const target = resources.workflows.find((w) => w.id === step.workflowId);
      return (
        <>
          <ResourceSelect
            id="wf-nested"
            label="Workflow"
            value={step.workflowId}
            items={resources.workflows.filter((w) => w.id !== selfId)}
            disabled={disabled}
            placeholder="Choose a workflow"
            onChange={(workflowId) => set({ workflowId })}
          />
          {target && (
            <p className="text-muted-foreground flex items-center gap-1 text-xs">
              Inputs:{' '}
              <span className="font-mono">
                {target.definition.inputs.map((i) => i.name).join(', ') || 'none'}
              </span>
              <Link
                to="/workflows/$workflowId"
                params={{ workflowId: target.id }}
                className="ml-auto inline-flex items-center gap-1 underline"
              >
                Open <ExternalLink className="size-3" aria-hidden />
              </Link>
            </p>
          )}
          <TemplateMapEditor
            key={`${step.id}-${step.workflowId}`}
            id="wf-nested-input"
            label="Input"
            value={step.input}
            disabled={disabled}
            onChange={(input) => set({ input })}
          />
          <p className="text-muted-foreground text-xs">
            The nested run must finish on its own: it cannot wait for a person or sleep for long.
          </p>
        </>
      );
    }
    case 'human':
      return (
        <>
          <div className="grid gap-1.5">
            <Label htmlFor="wf-human-message">Message</Label>
            <Textarea
              id="wf-human-message"
              className="min-h-20 font-mono text-xs"
              value={step.message}
              disabled={disabled}
              onChange={(e) => set({ message: e.target.value })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Form fields</Label>
            <InputsEditor
              inputs={step.fields}
              noun="field"
              disabled={disabled}
              onChange={(fields) => set({ fields })}
              hint="The run suspends here until someone fills in this form (on this page or through the API). The values are the step output."
            />
          </div>
        </>
      );
  }
}

function WhenEditor({
  step,
  disabled,
  set,
}: {
  step: WorkflowStep;
  disabled: boolean;
  set: (patch: Partial<WorkflowStep>) => void;
}) {
  return (
    <div className="grid gap-2 rounded-md border p-3">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          checked={Boolean(step.when)}
          disabled={disabled}
          onChange={(e) =>
            set({
              when: e.target.checked
                ? { match: 'all', rules: [{ left: '{{prev}}', operator: 'not_empty', right: '' }] }
                : undefined,
            })
          }
        />
        Run only when…
      </label>
      {step.when && (
        <>
          <ConditionEditor
            condition={step.when}
            disabled={disabled}
            onChange={(when) => set({ when })}
          />
          <p className="text-muted-foreground text-xs">Otherwise the step is skipped.</p>
        </>
      )}
    </div>
  );
}

// --- The inspector ---

const DURATION_UNITS = [
  { label: 'seconds', ms: 1000 },
  { label: 'minutes', ms: 60_000 },
  { label: 'hours', ms: 3_600_000 },
  { label: 'days', ms: 86_400_000 },
];
const bestUnit = (ms: number) =>
  [...DURATION_UNITS].reverse().find((u) => ms % u.ms === 0 && ms >= u.ms) ?? DURATION_UNITS[0]!;

export function StepInspector({
  definition,
  selected,
  resources,
  selfId,
  disabled,
  onChange,
  onSelect,
}: {
  definition: WorkflowDefinition;
  selected: string;
  resources: WorkflowResources;
  selfId: string;
  disabled: boolean;
  onChange: (definition: WorkflowDefinition) => void;
  onSelect: (id: string) => void;
}) {
  const index = definition.graph.findIndex(
    (e) =>
      e.id === selected ||
      (e.type === 'parallel' && e.steps.some((s) => s.id === selected)) ||
      (e.type === 'conditional' &&
        (e.branches.some((b) => b.step.id === selected) || e.fallback?.id === selected)) ||
      ((e.type === 'loop' || e.type === 'foreach') && e.step.id === selected),
  );
  if (index < 0)
    return <p className="text-muted-foreground text-sm">Select a node in the graph to edit it.</p>;
  const entry = definition.graph[index]!;
  const setEntry = (next: WorkflowEntry) =>
    onChange({ ...definition, graph: definition.graph.map((e, i) => (i === index ? next : e)) });
  const scope = entry.type === 'loop' ? 'loop' : entry.type === 'foreach' ? 'foreach' : undefined;

  // A step inside a container.
  if (entry.id !== selected) {
    const replaceChild = (next: WorkflowContainerStep) => {
      const swap = (s: WorkflowContainerStep) => (s.id === selected ? next : s);
      switch (entry.type) {
        case 'parallel':
          return setEntry({ ...entry, steps: entry.steps.map(swap) });
        case 'conditional':
          return setEntry({
            ...entry,
            branches: entry.branches.map((b) => ({ ...b, step: swap(b.step) })),
            ...(entry.fallback ? { fallback: swap(entry.fallback) } : {}),
          });
        case 'loop':
        case 'foreach':
          return setEntry({ ...entry, step: swap(entry.step) });
      }
    };
    const child =
      entry.type === 'parallel'
        ? entry.steps.find((s) => s.id === selected)!
        : entry.type === 'conditional'
          ? (entry.branches.find((b) => b.step.id === selected)?.step ?? entry.fallback!)
          : (entry as { step: WorkflowContainerStep }).step;
    return (
      <LeafInspector
        step={child}
        within={entry}
        definition={definition}
        index={index}
        scope={scope}
        resources={resources}
        selfId={selfId}
        disabled={disabled}
        onChange={(next) => {
          replaceChild(next as WorkflowContainerStep);
          if (next.id !== child.id) onSelect(next.id);
        }}
        onTypeChange={(type) => {
          const next = newLeaf(type, definition, resources, [], selfId) as WorkflowContainerStep;
          replaceChild({ ...next, id: child.id, name: child.name });
        }}
      />
    );
  }

  const header = (
    <EntryHeader
      entry={entry}
      index={index}
      count={definition.graph.length}
      disabled={disabled}
      onMove={(delta) => {
        const graph = [...definition.graph];
        const [moved] = graph.splice(index, 1);
        graph.splice(index + delta, 0, moved!);
        onChange({ ...definition, graph });
      }}
      onRemove={() => {
        onChange({ ...definition, graph: definition.graph.filter((_, i) => i !== index) });
        onSelect(definition.graph[index - 1]?.id ?? '__input');
      }}
    />
  );

  if (
    entry.type !== 'sleep' &&
    entry.type !== 'parallel' &&
    entry.type !== 'conditional' &&
    entry.type !== 'loop' &&
    entry.type !== 'foreach'
  ) {
    return (
      <div className="grid gap-4">
        {header}
        <LeafInspector
          step={entry}
          definition={definition}
          index={index}
          resources={resources}
          selfId={selfId}
          disabled={disabled}
          onChange={(next) => {
            setEntry(next);
            if (next.id !== entry.id) onSelect(next.id);
          }}
        />
      </div>
    );
  }

  const set = (patch: Partial<WorkflowEntry>) => setEntry({ ...entry, ...patch } as WorkflowEntry);
  const childButton = (step: WorkflowContainerStep, extra?: React.ReactNode) => {
    const Icon = ENTRY_META[step.type].icon;
    return (
      <div key={step.id} className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm">
        <Icon className="size-4" aria-hidden />
        <button
          type="button"
          className="min-w-0 flex-1 truncate text-left hover:underline"
          onClick={() => onSelect(step.id)}
        >
          {step.name} <span className="text-muted-foreground font-mono text-xs">{step.id}</span>
        </button>
        {extra}
      </div>
    );
  };
  const newChild = (taken: string[]) =>
    newLeaf('transform', definition, resources, taken) as WorkflowContainerStep;

  return (
    <div className="grid gap-4">
      {header}
      <NameAndId
        node={entry}
        disabled={disabled}
        onChange={(p) => {
          set(p);
          if (p.id) onSelect(p.id);
        }}
      />

      {entry.type === 'sleep' && (
        <>
          <Select
            value={entry.mode}
            onValueChange={(v) =>
              set(
                v === 'until'
                  ? {
                      mode: 'until',
                      until: entry.until ?? new Date(Date.now() + 3_600_000).toISOString(),
                    }
                  : { mode: 'duration', durationMs: entry.durationMs ?? 60_000 },
              )
            }
            disabled={disabled}
          >
            <SelectTrigger aria-label="Sleep mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="duration">Wait for a duration</SelectItem>
              <SelectItem value="until">Wait until a date</SelectItem>
            </SelectContent>
          </Select>
          {entry.mode === 'duration' ? (
            <DurationInput
              value={entry.durationMs ?? 0}
              disabled={disabled}
              onChange={(durationMs) => set({ durationMs })}
            />
          ) : (
            <div className="grid gap-1.5">
              <Label htmlFor="wf-sleep-until">Until (ISO date-time or a template)</Label>
              <Input
                id="wf-sleep-until"
                className="font-mono text-xs"
                value={entry.until ?? ''}
                disabled={disabled}
                onChange={(e) => set({ until: e.target.value })}
              />
            </div>
          )}
          <p className="text-muted-foreground text-xs">
            Short waits run in place. Longer ones park the run as “sleeping”; it continues on its
            own, even after a server restart.
          </p>
        </>
      )}

      {entry.type === 'parallel' && (
        <div className="grid gap-2">
          <Label>Steps (run at the same time)</Label>
          {entry.steps.map((s) =>
            childButton(
              s,
              <Button
                size="icon"
                variant="ghost-destructive"
                className="size-7"
                aria-label={`Remove ${s.name}`}
                disabled={disabled || entry.steps.length <= 2}
                onClick={() => set({ steps: entry.steps.filter((x) => x.id !== s.id) })}
              >
                <Trash2 />
              </Button>,
            ),
          )}
          <Button
            size="sm"
            variant="outline"
            className="justify-self-start"
            disabled={disabled || entry.steps.length >= 10}
            onClick={() => {
              const step = newChild(entry.steps.map((s) => s.id));
              set({ steps: [...entry.steps, step] });
              onSelect(step.id);
            }}
          >
            <Plus aria-hidden /> Add parallel step
          </Button>
          <p className="text-muted-foreground text-xs">The output is an object keyed by step id.</p>
        </div>
      )}

      {entry.type === 'conditional' && (
        <div className="grid gap-3">
          {entry.branches.map((b, i) => (
            <div key={b.step.id} className="grid gap-2 rounded-md border p-2">
              <div className="flex items-center justify-between text-xs font-medium">
                Branch {i + 1}: when…
                <Button
                  size="icon"
                  variant="ghost-destructive"
                  className="size-7"
                  aria-label={`Remove branch ${i + 1}`}
                  disabled={disabled || entry.branches.length === 1}
                  onClick={() => set({ branches: entry.branches.filter((_, j) => j !== i) })}
                >
                  <Trash2 />
                </Button>
              </div>
              <ConditionEditor
                condition={b.condition}
                disabled={disabled}
                label={`Branch ${i + 1} condition`}
                onChange={(condition) =>
                  set({
                    branches: entry.branches.map((x, j) => (j === i ? { ...x, condition } : x)),
                  })
                }
              />
              {childButton(b.step)}
            </div>
          ))}
          <Button
            size="sm"
            variant="outline"
            className="justify-self-start"
            disabled={disabled || entry.branches.length >= 10}
            onClick={() => {
              const step = newChild([
                ...entry.branches.map((b) => b.step.id),
                ...(entry.fallback ? [entry.fallback.id] : []),
              ]);
              set({
                branches: [
                  ...entry.branches,
                  {
                    condition: {
                      match: 'all',
                      rules: [{ left: '{{prev}}', operator: 'not_empty', right: '' }],
                    },
                    step,
                  },
                ],
              });
            }}
          >
            <Plus aria-hidden /> Add branch
          </Button>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(entry.fallback)}
              disabled={disabled}
              onChange={(e) => {
                if (e.target.checked)
                  set({ fallback: newChild(entry.branches.map((b) => b.step.id)) });
                else {
                  const { fallback: _drop, ...rest } = entry;
                  setEntry(rest);
                }
              }}
            />
            Otherwise, run a fallback step
          </label>
          {entry.fallback && childButton(entry.fallback)}
          <p className="text-muted-foreground text-xs">
            Every branch whose condition holds runs (at the same time); the others are skipped.
          </p>
        </div>
      )}

      {entry.type === 'loop' && (
        <div className="grid gap-3">
          <div className="grid grid-cols-[1fr_7rem] gap-2">
            <Select
              value={entry.mode}
              onValueChange={(v) => set({ mode: v as 'while' | 'until' })}
              disabled={disabled}
            >
              <SelectTrigger aria-label="Loop mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="until">Repeat until (do-until)</SelectItem>
                <SelectItem value="while">Repeat while (do-while)</SelectItem>
              </SelectContent>
            </Select>
            <Input
              aria-label="Max iterations"
              type="number"
              min={1}
              max={100}
              value={entry.maxIterations}
              disabled={disabled}
              onChange={(e) =>
                set({ maxIterations: Math.max(1, Math.min(100, Number(e.target.value) || 1)) })
              }
            />
          </div>
          <ConditionEditor
            condition={entry.condition}
            disabled={disabled}
            label="Loop condition"
            onChange={(condition) => set({ condition })}
          />
          {childButton(entry.step)}
          <p className="text-muted-foreground text-xs">
            The step runs at least once; the condition is checked after each run and can read{' '}
            <code>{'{{prev}}'}</code> (its last output) and <code>{'{{iteration}}'}</code>. Reaching
            the limit fails the loop.
          </p>
        </div>
      )}

      {entry.type === 'foreach' && (
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="wf-foreach-items">Items (a list)</Label>
            <Input
              id="wf-foreach-items"
              className="font-mono text-xs"
              value={entry.items}
              disabled={disabled}
              onChange={(e) => set({ items: e.target.value })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="wf-foreach-concurrency">Concurrency</Label>
            <Input
              id="wf-foreach-concurrency"
              type="number"
              min={1}
              max={10}
              value={entry.concurrency}
              disabled={disabled}
              onChange={(e) =>
                set({ concurrency: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })
              }
            />
          </div>
          {childButton(entry.step)}
          <p className="text-muted-foreground text-xs">
            The step runs once per item with <code>{'{{item}}'}</code> and{' '}
            <code>{'{{index}}'}</code>; the output is the list of results.
          </p>
        </div>
      )}
      <References definition={definition} index={index} />
    </div>
  );
}

function DurationInput({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled: boolean;
  onChange: (ms: number) => void;
}) {
  const [unit, setUnit] = useState(() => bestUnit(value));
  return (
    <div className="grid grid-cols-[1fr_8rem] gap-2">
      <Input
        aria-label="Duration"
        type="number"
        min={0}
        value={Math.round(value / unit.ms)}
        disabled={disabled}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0) * unit.ms)}
      />
      <Select
        value={unit.label}
        onValueChange={(label) => {
          const next = DURATION_UNITS.find((u) => u.label === label)!;
          setUnit(next);
          onChange(Math.round(value / unit.ms) * next.ms);
        }}
        disabled={disabled}
      >
        <SelectTrigger aria-label="Duration unit">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DURATION_UNITS.map((u) => (
            <SelectItem key={u.label} value={u.label}>
              {u.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function EntryHeader({
  entry,
  index,
  count,
  disabled,
  onMove,
  onRemove,
}: {
  entry: WorkflowEntry;
  index: number;
  count: number;
  disabled: boolean;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  const meta = ENTRY_META[entry.type];
  return (
    <div className="flex items-center gap-2">
      <meta.icon className="size-4" aria-hidden />
      <span className="text-sm font-medium">{meta.label}</span>
      <div className="ml-auto flex gap-1">
        <Button
          size="icon"
          variant="ghost"
          aria-label="Move up"
          disabled={disabled || index === 0}
          onClick={() => onMove(-1)}
        >
          <ArrowUp />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Move down"
          disabled={disabled || index === count - 1}
          onClick={() => onMove(1)}
        >
          <ArrowDown />
        </Button>
        <Button
          size="icon"
          variant="ghost-destructive"
          aria-label="Delete step"
          disabled={disabled || count === 1}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </div>
    </div>
  );
}

function LeafInspector({
  step,
  within,
  definition,
  index,
  scope,
  resources,
  selfId,
  disabled,
  onChange,
  onTypeChange,
}: {
  step: WorkflowStep;
  within?: WorkflowEntry;
  definition: WorkflowDefinition;
  index: number;
  scope?: 'loop' | 'foreach';
  resources: WorkflowResources;
  selfId: string;
  disabled: boolean;
  onChange: (step: WorkflowStep) => void;
  onTypeChange?: (type: WorkflowContainerStep['type']) => void;
}) {
  const set = (patch: Partial<WorkflowStep>) => onChange({ ...step, ...patch } as WorkflowStep);
  return (
    <div className="grid gap-4" key={step.id}>
      {within && (
        <div className="grid gap-2">
          <p className="text-muted-foreground text-xs">
            Inside <span className="text-foreground font-medium">{within.name}</span> (
            {ENTRY_META[within.type].label.toLowerCase()})
          </p>
          {onTypeChange && (
            <Select
              value={step.type}
              onValueChange={(v) => onTypeChange(v as WorkflowContainerStep['type'])}
              disabled={disabled}
            >
              <SelectTrigger aria-label="Step type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INNER_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {ENTRY_META[t].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
      <NameAndId node={step} disabled={disabled} onChange={(p) => set(p)} />
      <LeafFields step={step} resources={resources} selfId={selfId} disabled={disabled} set={set} />
      {step.type !== 'human' || !within ? (
        <WhenEditor step={step} disabled={disabled} set={set} />
      ) : null}
      <References definition={definition} index={index} scope={scope} />
    </div>
  );
}
